import type { FundAccountEngineConfigInfo } from '~/server_shared/openapi/data-contracts';
import { UpdateFundAccountEngineConfigParamsFundAccountDefaultBehavior } from '~/server_shared/openapi/data-contracts';

import { TEXTS } from '../texts';

/** Vocabulary shared by the read-only summary and the edit dialog. Extracted so neither imports
 *  the other — the card renders the dialog, so the dependency can only run one way. */

export type DefaultBehavior = UpdateFundAccountEngineConfigParamsFundAccountDefaultBehavior;

export const DEFAULT_BEHAVIOR = UpdateFundAccountEngineConfigParamsFundAccountDefaultBehavior;

/** The behaviors the API accepts, taken from the generated enum rather than re-listed. */
export const BEHAVIOR_VALUES: DefaultBehavior[] = Object.values(
  UpdateFundAccountEngineConfigParamsFundAccountDefaultBehavior
);

/** The mapping stays here because it is exhaustive over the enum — a behavior added on the BE side
 *  reds `tsc` here instead of reaching the menu as a raw `snake_case` key. The wording itself lives
 *  in `texts.ts`. */
export const BEHAVIOR_LABELS: Record<DefaultBehavior, string> = {
  [DEFAULT_BEHAVIOR.DefaultAccount]: TEXTS.BEHAVIOR_DEFAULT_ACCOUNT,
  [DEFAULT_BEHAVIOR.BlockPayment]: TEXTS.BEHAVIOR_BLOCK_PAYMENT,
  [DEFAULT_BEHAVIOR.SendEmpty]: TEXTS.BEHAVIOR_SEND_EMPTY,
};

export const BEHAVIOR_OPTIONS = BEHAVIOR_VALUES.map((value) => ({ value, label: BEHAVIOR_LABELS[value] }));

/** The read and write sides of the contract emit the same three values under two nominal enum
 *  names, so the stored behavior is resolved back through the write enum's own members instead of
 *  cast across — an unrecognised value (a behavior this build predates) falls back to unset. */
export const toDefaultBehavior = (value: string | null | undefined): DefaultBehavior | '' =>
  BEHAVIOR_VALUES.find((behavior) => behavior === value) ?? '';

/** `satisfies` rather than a bare `as const`: the two ids are also the engine-config field names,
 *  so a rename on the contract side should red this file rather than silently post an unknown key. */
export const FIELD_IDS = {
  DEFAULT_BEHAVIOR: 'default_behavior',
  DEFAULT_ACCOUNT_ID: 'default_account_id',
} as const satisfies Record<string, keyof FundAccountEngineConfigInfo>;

export interface DefaultBehaviorFormValues {
  default_behavior: DefaultBehavior | '';
  default_account_id: string;
}

export const initialFormValues = (config: FundAccountEngineConfigInfo): DefaultBehaviorFormValues => ({
  [FIELD_IDS.DEFAULT_BEHAVIOR]: toDefaultBehavior(config.default_behavior),
  [FIELD_IDS.DEFAULT_ACCOUNT_ID]: config.default_account_id ?? '',
});
